import type {
  DesktopDownloadChannel,
  DesktopDownloads,
  DesktopUpdateChannel,
} from '@crowdex/agent-contracts/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { ChevronDown, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SettingRow, settingButtonClass } from '@/components/settings-page/setting-controls';
import { failureReason } from '@/lib/conversations/precondition-failure';
import {
  browserDesktopOs,
  desktopDownloadHref,
  formatInstallerSize,
  INSTALLER_LABELS,
  loadDesktopDownloads,
  preferredInstaller,
} from '@/lib/desktop-downloads';
import { errorMessage } from '@/lib/error-message';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; downloads: DesktopDownloads }
  | { kind: 'unlinked' }
  | { kind: 'failed'; message: string };

const CHANNEL_NAMES: Record<DesktopUpdateChannel, string> = {
  stable: 'Stable',
  prerelease: 'Beta',
};

function InstallerItems({
  channel,
  release,
}: {
  channel: DesktopUpdateChannel;
  release: DesktopDownloadChannel;
}) {
  return (
    <>
      <DropdownMenuLabel className="type-meta font-normal text-muted-foreground">
        {CHANNEL_NAMES[channel]} · {release.version}
      </DropdownMenuLabel>
      {release.installers.map((installer) => {
        const label = INSTALLER_LABELS[installer.target];
        return (
          <DropdownMenuItem asChild className="py-2" key={installer.target}>
            <a download href={desktopDownloadHref(installer.target, channel)}>
              <span className="min-w-0 flex-1">
                <span className="block text-foreground">{label.os}</span>
                <span className="block text-muted-foreground text-xs">
                  {label.hint} · {formatInstallerSize(installer.size)}
                </span>
              </span>
            </a>
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

/**
 * Settings › General in a browser: Crowdex Desktop for this computer. The installer of its
 * system comes first; every other one (and the beta, when there is a newer one) is in the menu.
 */
export function DesktopDownloadSettings() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const os = browserDesktopOs();

  useEffect(() => {
    let current = true;
    loadDesktopDownloads().then(
      (downloads) => current && setState({ kind: 'ready', downloads }),
      (cause: unknown) => {
        if (!current) return;
        setState(
          failureReason(cause) === 'cloud_unlinked'
            ? { kind: 'unlinked' }
            : { kind: 'failed', message: errorMessage(cause) },
        );
      },
    );
    return () => {
      current = false;
    };
  }, []);

  const title = 'Crowdex Desktop';
  const purpose = 'Runs agents on this computer, in your checkouts.';
  if (os === null) return null;
  if (state.kind === 'loading')
    return <SettingRow description={`${purpose} Looking for the latest release…`} title={title} />;
  if (state.kind === 'unlinked')
    return (
      <SettingRow
        description={`${purpose} Link Crowdex Cloud under Account to download it.`}
        title={title}
      />
    );
  if (state.kind === 'failed')
    return (
      <SettingRow description={`Could not list the downloads: ${state.message}`} title={title} />
    );

  const { stable, prerelease } = state.downloads;
  // Without a stable release yet, the beta is what there is.
  const channel: DesktopUpdateChannel = stable ? 'stable' : 'prerelease';
  const release = stable ?? prerelease;
  if (release === null)
    return <SettingRow description={`${purpose} No release is published yet.`} title={title} />;
  const installer = preferredInstaller(release, os);
  const label = installer === null ? null : INSTALLER_LABELS[installer.target];

  return (
    <SettingRow
      description={
        installer === null || label === null
          ? `${purpose} Version ${release.version} is not published for this system yet.`
          : `${purpose} Version ${release.version}${channel === 'prerelease' ? ' (beta)' : ''} · ${label.hint} · ${formatInstallerSize(installer.size)}`
      }
      title={title}
    >
      {installer !== null && label !== null && (
        <a
          className={settingButtonClass('primary')}
          download
          href={desktopDownloadHref(installer.target, channel)}
        >
          <Download aria-hidden className="size-3.5" />
          Download for {label.os}
        </a>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Other downloads"
          className={settingButtonClass(installer === null ? 'primary' : 'outline')}
        >
          {installer === null ? 'Downloads' : 'Other'}
          <ChevronDown aria-hidden className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <InstallerItems channel={channel} release={release} />
          {stable !== null && prerelease !== null && (
            <>
              <DropdownMenuSeparator />
              <InstallerItems channel="prerelease" release={prerelease} />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </SettingRow>
  );
}
